import {
  AfterViewInit,
  ApplicationRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {EmployeeAttributeHttpService} from "../x-services/employee-attribute-http.service";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AlertLevel} from "../../../enums/alert-level";
import {PermissionService} from "../../../_auth/permission.service";
import {EmployeeDto} from "../../company/x-models/employee-dto";
import {EmployeeAttributeNl} from "../../company/x-models/employee-attribute-nl";
import {AppRoutes} from "../../../enums/routes";
import {CompanyRoutes} from "../../company/x-models/x-enums/company-routes";
import {EmployeeNl} from "../../company/x-models/employee-nl";
import {Side} from "../../company/x-models/x-enums/side";
import {EmployeeAttributeService} from "../../company/_services/_dto-services/employee-attribute.service";
import {Router} from "@angular/router";
import {FormsModule} from "@angular/forms";

@Component({
  selector: 'app-employee-attribute-popup',
  templateUrl: './employee-attribute-popup.component.html',
  // The template uses only the built-in control flow (@if/@for) plus plain class bindings, so CommonModule
  // is not needed here — FormsModule is, for the attribute checkboxes and the "new attribute" field.
  imports: [
    FormsModule
  ],
  styleUrls: ['./employee-attribute-popup.component.scss']
})
export class EmployeeAttributePopupComponent<T extends EmployeeDto | EmployeeNl> implements OnChanges, OnInit, AfterViewInit {
  @Input() mouseEvent?: MouseEvent;
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() employeeChange = new EventEmitter<EmployeeDto>();
  @Input() employee: T | undefined;
  @Input() side: Side = Side.Right;
  @Input() redirectPath: any = [];
  @Input() queryParams: any = {};
  @Input() useRelativeX: boolean = false;
  @Input() useRelativeY: boolean = false;
  @Input() prefix: string = "";
  @Output() heightChange = new EventEmitter<number>();
  /**
   * The popup's own box. A `#popup` template reference on a plain element resolves to an `ElementRef`, never to
   * the `HTMLElement` this used to be typed as — the two `(this.popup as any).nativeElement` casts below were
   * that mistype being worked around at every call site.
   */
  @ViewChild("popup") popup?: ElementRef<HTMLElement>;
  @Input() relative: HTMLElement | undefined;

  //employeeAttributes?: EmployeeAttributeDto[] = [];
  popupHeight: number = 0;
  positionX: number = 0;
  positionY: number = 0;
  padding: number = 0;
  editMode: boolean = false;
  editNewName: string = "";
  protected readonly Routes = AppRoutes;
  protected readonly CompanyRoutes = CompanyRoutes;
  protected readonly Side = Side;

  constructor(private employeeAttributeHttpService: EmployeeAttributeHttpService,
              private globalAlertService: GlobalAlertService,
              public PermissionService: PermissionService,
              public EmployeeAttributeService: EmployeeAttributeService,
              private appRef: ApplicationRef, private router: Router) {
  }

  ngAfterViewInit(): void {
    let obs = new ResizeObserver(entries => {
      for (let entry of entries) {
        const cr = entry.contentRect;
        if (cr.height != this.popupHeight) {
          this.popupHeight = cr.height;
          this.getPaddingHeight()
        } else {
          this.popupHeight = cr.height;
        }
      }
    });
    // THIS instance's box, via the view child — not `document.getElementById("eaPopup")`, which returns the
    // first one in the document and therefore has every popup on a page observing the same element.
    const own = this.popup?.nativeElement;
    if (own) obs.observe(own);
  }

  @HostListener("document:click", ['$event'])
  onClick(event: MouseEvent) {
    //this.heightChange.emit(this.height)

    if (!event.target) {
      this.visibleChange.emit(false);
      return;
    }

    let sender: any = event.target;
    while (sender && ["eaPopup", "eaToggle", "eaDisEdit", "eaEdit"].indexOf(sender.id) == -1) {
      sender = sender.offsetParent ?? sender.parentElement ?? undefined
    }
    if (!this.useRelativeY || !this.useRelativeX) this.getPaddingHeight()

    if (!sender) {
      this.visibleChange.emit(false);
      this.editMode = false;
      this.editNewName = "";
    }
  }

  isOnPopup(element: any, event: MouseEvent): boolean {
    if (event.pageX >= this.positionX && event.pageX <= this.positionX + element.nativeElement.clientWidth &&
      event.pageY >= this.positionY && event.pageY <= this.positionY + element.nativeElement.clientHeight) {
      return this.visible;
    }
    return false;
  }

  /**
   * The box the popup has to stay inside, or `undefined` when there is none.
   *
   * `[relative]` is normally bound — both hosts pass the app shell's scroll container
   * (`AlignmentService.MainWindow`) — and the walk below is only the fallback for a host that does not. That
   * walk climbs `offsetParent ?? parentElement` looking for `id="eaRelative"` and CAN legitimately end at the
   * top of the document without finding one; the popup is a sibling of the employees table, not a descendant
   * of it, so nothing but a bound `[relative]` makes that search succeed there.
   *
   * The result used to be dereferenced unguarded by both callers, so that ordinary outcome surfaced as
   * `TypeError: Cannot read properties of undefined (reading 'clientHeight')` thrown out of a
   * `document:click` handler — which takes the whole change-detection turn of that click down with it, so the
   * page simply stops updating and the cause looks like a data problem. Returning `undefined` and letting each
   * caller say what "no container" means is the fix; see the two call sites.
   */
  private findRelativeContainer(): HTMLElement | undefined {
    if (this.relative) return this.relative;

    let candidate: HTMLElement | undefined = this.popup?.nativeElement;
    while (candidate && candidate.id != "eaRelative") {
      candidate = (candidate.offsetParent as HTMLElement | null) ?? candidate.parentElement ?? undefined;
    }
    return candidate ?? undefined;
  }

  getPaddingHeight(): number {
    if (!this.useRelativeX && !this.useRelativeY) return 0;

    const relative = this.findRelativeContainer();
    // No enclosing container means nothing can clip the popup, so there is no spacer to grow: the padding is
    // whatever a container of unbounded height would ask for, which is none.
    if (!relative) {
      if (this.padding !== 0) {
        this.padding = 0;
        this.appRef.tick();
      }
      return 0;
    }

    let height = relative.clientHeight - this.padding;
    let popupSum = this.popupHeight + this.positionY;

    if (height < popupSum) {
      this.padding = popupSum - height + 30;
    } else {
      this.padding = 0;
    }

    this.appRef.tick();

    return this.padding;
  }

  ngOnInit(): void {

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mouseEvent && this.mouseEvent) {
      this.repositionPopup()
    }
  }

  @HostListener("window:resize", [])
  repositionPopup() {
    this.editMode = false;
    this.editNewName = "";
    if (!this.mouseEvent || !this.mouseEvent.target) return;
    // Walk up from the click target to the "eaToggle" trigger to anchor the popup. Guard against the target
    // not living under an eaToggle element: without the null-check the loop runs off the top of the DOM and
    // dereferences null.id, crashing the whole page (the info-icon trigger must carry id="eaToggle").
    let sender: HTMLElement | null = this.mouseEvent.target as HTMLElement;
    while (sender && sender.id != "eaToggle") {
      sender = sender.parentElement;
    }
    if (!sender) return;

    const relative = this.findRelativeContainer();

    this.positionX = this.side == Side.Right ? sender.getBoundingClientRect().left + sender.clientWidth + 20 : sender.getBoundingClientRect().left - 256 - 20;
    this.positionY = sender.getBoundingClientRect().top + window.pageYOffset + sender.clientHeight / 2 - 30;

    // With no container the coordinates above are already the ones to use: they are page coordinates, and the
    // adjustments below exist only to re-express them relative to a container that here does not exist.
    if (relative) {
      if (this.useRelativeX) {
        this.positionX -= relative.getBoundingClientRect().left

        if (!this.useRelativeY) {
          this.positionY -= relative.getBoundingClientRect().top + window.pageYOffset;
        }
      }
      if (this.useRelativeY) {
        this.positionY -= relative.getBoundingClientRect().top - relative.scrollTop
      }
    }

    //this.heightChange.emit(this.popup.offsetHeight);
  }

  addEmployeeAttribute(event: MouseEvent) {
    event.preventDefault()
    if (!this.editMode || !this.employee || !this.EmployeeAttributeService.Entities) return;
    this.employeeAttributeHttpService.add(this.editNewName, [this.employee.id]).subscribe(x => {
      this.EmployeeAttributeService.Replace(x);
      this.employee!.employeeAttributes.push(x as EmployeeAttributeNl)
      this.editNewName = "";
    }, error => {
      this.globalAlertService.createAlertBannerModel("Fehler", "Attribut konnte nicht erstellt werden.", AlertLevel.error, 2000);
      this.globalAlertService.show();
    })
  }

  setEmployeeAttributes(employeeAttributeIds: string[], state: boolean) {
    if (!this.employee || !this.EmployeeAttributeService.Entities) return;
    this.employeeAttributeHttpService.setEmployeeAttributes(employeeAttributeIds, [this.employee.id], state).subscribe(x => {
      if (x.item1) {
        x.item1.forEach(c => {
          this.EmployeeAttributeService.Replace(c);
        })
      }
      if (x.item2) {
        x.item2.forEach(c => {
          this.employee!.employeeAttributes = c.employeeAttributes;
        })
      }
    }, error => {
      this.globalAlertService.createAlertBannerModel("Fehler", "Attribut konnte nicht gesetzt werden.", AlertLevel.error, 2000);
      this.globalAlertService.show();
      this.visibleChange.emit(false);
      this.employeeChange.emit(undefined);
      this.editNewName = "";
      this.editMode = false;
    })
  }

  redirectToSettings(id: string) {
    this.queryParams = this.getQueryParams(id);
    this.router.navigate(['/' + AppRoutes.CompanyModule + '/' + CompanyRoutes.EmployeeAttributesSettings], {queryParams: this.queryParams});
  }

  getQueryParams(id: string): any {
    this.queryParams["id"] = id;
    if (this.redirectPath.length > 0) {
      this.queryParams["redirect"] = this.redirectPath;
    }
    return this.queryParams;
  }

  containsAttribute(id: string): boolean {
    if (!this.employee) return false;
    return this.employee.employeeAttributes.findIndex(x => x.id == id) > -1;
  }
}
